import { useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, Plus, Trash2, X, AlertCircle, CreditCard, Armchair } from 'lucide-react'
import { isAxiosError } from 'axios'
import {
  getLicencePool,
  setLicencePool,
  getSeats,
  allocateSeat,
  reclaimSeat,
  getRolePack,
} from '../../services/api'
import type { LicencePool, Seat, RolePackEntry } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { useActiveOrg } from '../../hooks/useActiveOrg'
import AdminGate from '../../components/organisation/AdminGate'
import { useOrgType } from '../../contexts/OrgTypeContext'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import { shortDate } from '../../lib/shortDate'

type LicenceClass = 'full' | 'contributor' | 'viewer'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LICENCE_RANK: Record<LicenceClass, number> = { viewer: 0, contributor: 1, full: 2 }

const LICENCE_LABEL: Record<LicenceClass, string> = {
  full: 'Full seat',
  contributor: 'Contributor seat',
  viewer: 'Viewer (free) seat',
}

// Friendly labels for the real org_type codes (mirrors the map used on the
// platform-admin Organisations screen) — shown as the header status chip.
const ORG_TYPE_LABEL: Record<string, string> = {
  vc_fund: 'Venture Capital Fund',
  ecosystem_convener: 'Ecosystem / Convener',
  corporate_industry: 'Corporate / Industry',
  corporate: 'Corporate / Industry',
  research_institution: 'Research Institution',
  government_agency: 'Government Agency',
  accelerator_incubator: 'Accelerator / Incubator',
  inventor_personal: 'Personal workspace',
  personal: 'Personal workspace',
}

// Fig54's Member column is a person's name and the Email column their address.
// Seat rows carry no separate name field, so derive a readable handle from the
// email local-part (e.g. "priya.kapoor@x" → "Priya Kapoor"). This is a display
// transform of the REAL email, never invented identity data; it falls back to
// the raw value when there's nothing to humanise.
function memberDisplayName(emailOrId: string): string {
  const value = emailOrId.trim()
  if (!value.includes('@')) return value
  const local = value.split('@')[0]
  const words = local.split(/[._-]+/).filter(Boolean)
  if (words.length === 0) return value
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

function avatarInitials(emailOrName: string): string {
  const value = emailOrName.trim()
  if (!value) return '?'
  if (value.includes('@')) {
    const local = value.split('@')[0]
    const parts = local.split(/[._-]+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return local.slice(0, 2).toUpperCase()
  }
  const parts = value.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return value.slice(0, 2).toUpperCase()
}

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined
    return data?.error || data?.message || err.message || fallback
  }
  if (err instanceof Error) return err.message
  return fallback
}

function licenceBadgeClass(licence: LicenceClass): string {
  switch (licence) {
    case 'full':
      return 'bg-blue-500/20 text-blue-700'
    case 'contributor':
      return 'bg-iflow-100 text-iflow-700'
    case 'viewer':
    default:
      return 'bg-gray-500/20 text-gray-600'
  }
}

// The backend only exposes a single destructive op — DELETE /licensing/seats
// (reclaimSeat) — which frees the licence and, for an invited-not-yet-active
// seat, also withdraws the invite. There is no separate "revoke invite" /
// "suspend" / "reactivate" endpoint (Story #… not yet built), so rather than
// fabricate buttons that don't do anything real, the ONE real action is
// labelled per the seat's current state to stay honest about what it does.
function seatActionLabel(state: Seat['state']): string {
  if (state === 'invited') return 'Revoke invite'
  if (state === 'suspended') return 'Remove'
  return 'Reclaim seat'
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function MembersAndLicences() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { orgId, membership: activeMembership, orgName, isOrgAdmin, isPlatformAdmin, isPersonalOrg } = useActiveOrg()
  // BUG-146: never hard-code a 'vc_fund' fallback — that shipped the wrong role
  // pack for every org whose membership lacked an org_type. Resolve the real org
  // type from the authoritative OrgTypeContext (backed by GET /users/me), and
  // only use the membership's value when it is explicitly present.
  const { orgType: contextOrgType } = useOrgType()
  const orgType = activeMembership?.org_type || contextOrgType

  const [inviteOpen, setInviteOpen] = useState(false)
  const [poolOpen, setPoolOpen] = useState(false)
  const [reclaimTarget, setReclaimTarget] = useState<Seat | null>(null)

  // ---- Queries (hooks must run unconditionally) -------------------------
  const {
    data: pool,
    isLoading: poolLoading,
    error: poolError,
    refetch: refetchPool,
  } = useQuery({
    queryKey: ['licence-pool', orgId],
    queryFn: () => getLicencePool(orgId as string),
    enabled: !!orgId && isOrgAdmin,
  })

  const {
    data: seats,
    isLoading: seatsLoading,
    error: seatsError,
    refetch: refetchSeats,
  } = useQuery({
    queryKey: ['seats', orgId],
    queryFn: () => getSeats(orgId as string),
    enabled: !!orgId && isOrgAdmin,
  })

  const { data: rolePack } = useQuery({
    queryKey: ['role-pack', orgType],
    queryFn: () => getRolePack(orgType),
    enabled: !!orgId && isOrgAdmin,
  })

  // BUG-121 / P7 honesty: reclaim failures are surfaced by ConfirmDialog itself —
  // onConfirm throws, so the dialog stays open and shows the real error instead of
  // faking success. Success invalidates and closes.
  const reclaimMutation = useMutation({
    mutationFn: (seatId: string) => reclaimSeat(orgId as string, seatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seats', orgId] })
      queryClient.invalidateQueries({ queryKey: ['licence-pool', orgId] })
      setReclaimTarget(null)
    },
  })

  // ---- Access gate -------------------------------------------------------
  if (!isOrgAdmin || !orgId) {
    return (
      <AdminGate
        area="members and licences"
        orgName={isPersonalOrg ? undefined : orgName}
        isPlatformAdmin={isPlatformAdmin}
      />
    )
  }

  // A failed load replaces the whole body: no Invite button, seat bar or
  // "No members yet" empty state may sit next to an outage (UX-042).
  const loadError = poolError ?? seatsError
  if (loadError) {
    return (
      <AdminGate
        area="members and licences"
        orgName={isPersonalOrg ? undefined : orgName}
        error={loadError}
        onRetry={() => {
          void refetchPool()
          void refetchSeats()
        }}
      />
    )
  }

  const isLoading = poolLoading || seatsLoading

  const visibleSeats = (seats ?? []).filter((s) => s.state !== 'removed')

  // Derived pool figures (safe defaults until pool loads)
  const fullPurchased = pool?.seats_purchased_full ?? 0
  const fullAllocated = pool?.seats_allocated_full ?? 0
  const contribPurchased = pool?.seats_purchased_contributor ?? 0
  const contribAllocated = pool?.seats_allocated_contributor ?? 0
  const fullAvailable = Math.max(0, fullPurchased - fullAllocated)
  const contribAvailable = Math.max(0, contribPurchased - contribAllocated)

  const availabilityFor = (licence: LicenceClass): number | null => {
    if (licence === 'full') return fullAvailable
    if (licence === 'contributor') return contribAvailable
    return null // viewer is free / uncapped seat-wise
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={isPersonalOrg ? 'Your plan & collaborators' : 'Members & Licences'}
        description={
          isPersonalOrg
            ? "This is your own plan, not a shared organisation. Buy seats and invite the collaborators you work with; they get a seat from your pool."
            : `Invite people to your organisation, give them a role, and assign them a licence seat. Roles control what a member can do; a seat is what their licence tier consumes.${
                orgName ? ` · ${orgName}` : ''
              }`
        }
        helpKey="org-members"
        status={orgType ? { label: ORG_TYPE_LABEL[orgType] ?? orgType, tone: 'info' } : undefined}
        secondaryActions={
          <button
            type="button"
            onClick={() => setPoolOpen(true)}
            className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50"
          >
            <CreditCard size={16} /> Manage seats
          </button>
        }
        primaryAction={
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> Invite member
          </button>
        }
      />

      {/* Seat usage — one combined bar across the paid tiers + a free-Viewer count,
          matching the mockup's single "Seat usage" panel rather than four separate
          stat cards. All figures come straight from the real pool/seats queries. */}
      <SeatUsageBar
        fullPurchased={fullPurchased}
        fullAllocated={fullAllocated}
        contribPurchased={contribPurchased}
        contribAllocated={contribAllocated}
        viewerCount={pool?.viewer_count ?? 0}
        planTier={pool?.plan_tier ?? null}
        pendingInvites={visibleSeats.filter((s) => s.state === 'invited').length}
        loading={poolLoading}
      />

      {/* Members table */}
      <div className="light-card p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-4 lg:p-6">
            <LoadingSkeleton rows={5} type="table" />
          </div>
        ) : visibleSeats.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No members yet"
            description="Invite your first team member to allocate a licence seat from your pool."
            action={
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                <Plus size={16} /> Invite member
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-iflow-100 bg-iflow-900">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white">
                    Member
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white">
                    Email
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white">
                    Role
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white">
                    Licence
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white">
                    Since
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-white text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleSeats.map((seat) => {
                  const isActive = seat.state === 'active'
                  const isSelf = !!user?.id && seat.member_id === user.id
                  // Bug #5897 (F17): a seat with no member_email fell all the
                  // way back to the raw member_id UUID (and an em-dash for
                  // email), while every seat created through the invite flow
                  // always has one. For the viewer's own row, the
                  // authenticated session already knows who that is — use
                  // it instead of a UUID nobody can read.
                  const email = seat.member_email || (isSelf ? user?.email : undefined)
                  const display = email || (isSelf ? user?.name : undefined) || seat.member_id
                  const name = email ? memberDisplayName(email) : display
                  return (
                    <tr key={seat.seat_id} className="border-b border-gray-100 last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-iflow-100 text-iflow-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {avatarInitials(display)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-800 truncate">{name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-[220px]">
                        {email || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {seat.role_display || seat.role || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                            seat.state === 'invited'
                              ? 'bg-gray-100 text-gray-400'
                              : licenceBadgeClass(seat.licence_class)
                          }`}
                        >
                          {seat.state === 'invited' ? '— no seat —' : LICENCE_LABEL[seat.licence_class]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center text-sm text-gray-700">
                          <span
                            className={`w-2 h-2 rounded-full mr-2 ${
                              isActive ? 'bg-green-500' : seat.state === 'invited' ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                          />
                          {isActive ? 'Active' : seat.state === 'invited' ? 'Invited' : 'Suspended'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {seat.allocated_at ? shortDate(seat.allocated_at) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isSelf ? (
                          <span className="text-xs text-gray-400">You</span>
                        ) : (
                          <button
                            type="button"
                            aria-label={`${seatActionLabel(seat.state)} for ${display}`}
                            title={seatActionLabel(seat.state)}
                            onClick={() => setReclaimTarget(seat)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50"
                          >
                            <Trash2 size={14} /> {seatActionLabel(seat.state)}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Roles vs licences — always shown (Fig54): a role governs WHAT a member
          can do, a licence tier is WHICH seat they consume. Folds in the real
          role-pack list for this org type when it has loaded. */}
      <div className="light-card p-4 lg:p-6 bg-iflow-50/60 border border-iflow-100">
        <p className="text-sm text-iflow-900">
          <span className="font-semibold">Roles vs licences.</span> A <b>role</b>
          {rolePack && rolePack.length > 0 ? (
            <> ({rolePack.map((r) => r.display).join(' · ')})</>
          ) : null}{' '}
          sets what someone can do in the org. A <b>licence tier</b> (
          {LICENCE_LABEL.full} · {LICENCE_LABEL.contributor} · {LICENCE_LABEL.viewer}) is the seat
          they consume — Viewer seats are free and don't count against your purchased pool.
          Reclaiming a seat frees the licence.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Role changes take effect at the member's next sign-in.
        </p>
      </div>

      {/* Modals */}
      {inviteOpen && (
        <InviteModal
          orgId={orgId}
          orgType={orgType}
          rolePack={rolePack ?? []}
          availabilityFor={availabilityFor}
          onClose={() => setInviteOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['seats', orgId] })
            queryClient.invalidateQueries({ queryKey: ['licence-pool', orgId] })
            setInviteOpen(false)
          }}
        />
      )}

      {poolOpen && (
        <BuySeatsModal
          orgId={orgId}
          pool={pool}
          onClose={() => setPoolOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['licence-pool', orgId] })
            setPoolOpen(false)
          }}
        />
      )}

      <ConfirmDialog
        open={!!reclaimTarget}
        title={reclaimTarget ? `${seatActionLabel(reclaimTarget.state)}?` : 'Reclaim seat?'}
        message={
          reclaimTarget
            ? reclaimTarget.state === 'invited'
              ? `This withdraws the pending invite for ${
                  reclaimTarget.member_email || reclaimTarget.member_id
                } and returns the reserved ${reclaimTarget.licence_class} licence to your pool.`
              : `This will remove ${
                  reclaimTarget.member_email || reclaimTarget.member_id
                } and return their ${reclaimTarget.licence_class} licence to your pool.`
            : ''
        }
        confirmLabel={reclaimTarget ? seatActionLabel(reclaimTarget.state) : 'Reclaim seat'}
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={async () => {
          if (!reclaimTarget) return
          try {
            await reclaimMutation.mutateAsync(reclaimTarget.seat_id)
          } catch (err) {
            throw new Error(errorMessage(err, 'Failed to reclaim the seat. Please try again.'))
          }
        }}
        onClose={() => setReclaimTarget(null)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seat usage — one combined bar (Fig54) instead of four separate stat cards.
// ---------------------------------------------------------------------------

function SeatUsageBar({
  fullPurchased,
  fullAllocated,
  contribPurchased,
  contribAllocated,
  viewerCount,
  planTier,
  pendingInvites,
  loading,
}: {
  fullPurchased: number
  fullAllocated: number
  contribPurchased: number
  contribAllocated: number
  viewerCount: number
  planTier: string | null
  pendingInvites: number
  loading: boolean
}) {
  const totalPurchased = fullPurchased + contribPurchased
  const totalAllocated = fullAllocated + contribAllocated
  const totalAvailable = Math.max(0, totalPurchased - totalAllocated)
  const pct =
    totalPurchased > 0 ? Math.min(100, Math.round((totalAllocated / totalPurchased) * 100)) : 0

  return (
    <div className="light-card p-4 lg:p-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
          <Armchair size={16} className="text-iflow-600" /> Seat usage
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-700">
            {loading ? (
              '—'
            ) : (
              <>
                <span className="text-base font-bold text-gray-800">{totalAllocated}</span> of{' '}
                <span className="font-semibold">{totalPurchased}</span> seats used ·{' '}
                <span className="font-semibold text-emerald-700">{totalAvailable} available</span>
              </>
            )}
          </p>
          {planTier && <p className="text-xs text-gray-400">Plan: {planTier}</p>}
        </div>
      </div>
      <div className="h-2.5 rounded-full bg-iflow-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-iflow-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
          <LegendDot dotClass="bg-gray-500" label={`${fullAllocated} ${LICENCE_LABEL.full}`} />
          <LegendDot
            dotClass="bg-iflow-500"
            label={`${contribAllocated} ${LICENCE_LABEL.contributor}`}
          />
          <LegendDot dotClass="bg-gray-300" label={`${viewerCount} ${LICENCE_LABEL.viewer}`} />
        </div>
        {pendingInvites > 0 && (
          <span className="text-xs font-medium text-amber-700">
            {pendingInvites} invite{pendingInvites === 1 ? '' : 's'} pending — seat
            {pendingInvites === 1 ? '' : 's'} reserved
          </span>
        )}
      </div>
    </div>
  )
}

function LegendDot({ dotClass, label }: { dotClass: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${dotClass}`} />
      {label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Invite modal
// ---------------------------------------------------------------------------

function InviteModal({
  orgId,
  orgType,
  rolePack,
  availabilityFor,
  onClose,
  onSuccess,
}: {
  orgId: string
  orgType: string
  rolePack: RolePackEntry[]
  availabilityFor: (licence: LicenceClass) => number | null
  onClose: () => void
  onSuccess: () => void
}) {
  const [email, setEmail] = useState('')
  const [roleDisplay, setRoleDisplay] = useState(rolePack[0]?.display ?? '')
  const [licenceClass, setLicenceClass] = useState<LicenceClass>('full')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const selectedRole = rolePack.find((r) => r.display === roleDisplay)

  // Client-side rule: chosen licence must meet the role's minimum licence rank.
  const meetsMinLicence =
    !selectedRole || LICENCE_RANK[licenceClass] >= LICENCE_RANK[selectedRole.min_licence]

  const allocate = useMutation({
    mutationFn: () =>
      allocateSeat({
        org_id: orgId,
        member_id: email.trim(),
        member_email: email.trim(),
        licence_class: licenceClass,
        role: selectedRole?.platform_role ?? roleDisplay,
        org_type: orgType,
      }),
    onSuccess,
    onError: (err) => {
      setSubmitError(errorMessage(err, 'Failed to invite member. Please try again.'))
    },
  })

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
  const classAvailability = availabilityFor(licenceClass)
  const classExhausted = classAvailability !== null && classAvailability <= 0
  const canSubmit = emailValid && !!roleDisplay && meetsMinLicence && !classExhausted

  const handleSubmit = () => {
    setSubmitError(null)
    if (!canSubmit) return
    allocate.mutate()
  }

  const licenceOptions: LicenceClass[] = ['full', 'contributor', 'viewer']

  return (
    <ModalShell title="Invite member" icon={<Plus size={18} />} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-email">
            Email
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
          {email && !emailValid && (
            <p className="text-xs text-red-600 mt-1">Enter a valid email address.</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-role">
            Role
          </label>
          <select
            id="invite-role"
            value={roleDisplay}
            onChange={(e) => setRoleDisplay(e.target.value)}
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-iflow-500"
          >
            {rolePack.length === 0 && <option value="">No roles available</option>}
            {rolePack.map((r) => (
              <option key={r.display} value={r.display}>
                {r.display}
              </option>
            ))}
          </select>
          {selectedRole && (
            <p className="text-xs text-gray-500 mt-1">
              Requires at least a {selectedRole.min_licence} licence.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-licence">
            Licence class
          </label>
          <select
            id="invite-licence"
            value={licenceClass}
            onChange={(e) => setLicenceClass(e.target.value as LicenceClass)}
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-iflow-500"
          >
            {licenceOptions.map((lc) => {
              const avail = availabilityFor(lc)
              // Disable paid classes that have 0 available.
              const disabled = avail !== null && avail <= 0
              const availText = avail === null ? 'free' : `${avail} available`
              return (
                <option key={lc} value={lc} disabled={disabled}>
                  {lc.charAt(0).toUpperCase() + lc.slice(1)} — {availText}
                </option>
              )
            })}
          </select>
        </div>

        {selectedRole && !meetsMinLicence && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              The role "{selectedRole.display}" needs at least a {selectedRole.min_licence} licence.
              Choose a higher licence class.
            </p>
          </div>
        )}

        {classExhausted && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              No {licenceClass} seats available. Buy more seats to invite this member.
            </p>
          </div>
        )}

        {submitError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
            <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">
              {submitError}
              {/^.*(exhaust|pool|no .*seat|seats).*$/i.test(submitError)
                ? ' — buy more seats to continue.'
                : ''}
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || allocate.isPending}
          className="bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {allocate.isPending ? 'Inviting…' : 'Send invite'}
        </button>
      </div>
    </ModalShell>
  )
}

// ---------------------------------------------------------------------------
// Buy seats modal
// ---------------------------------------------------------------------------

function BuySeatsModal({
  orgId,
  pool,
  onClose,
  onSuccess,
}: {
  orgId: string
  pool: LicencePool | undefined
  onClose: () => void
  onSuccess: () => void
}) {
  const [fullSeats, setFullSeats] = useState<string>(String(pool?.seats_purchased_full ?? 0))
  const [contribSeats, setContribSeats] = useState<string>(
    String(pool?.seats_purchased_contributor ?? 0),
  )
  const [planTier, setPlanTier] = useState<string>(pool?.plan_tier ?? '')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () =>
      setLicencePool(orgId, {
        seats_purchased_full: Number(fullSeats) || 0,
        seats_purchased_contributor: Number(contribSeats) || 0,
        plan_tier: planTier.trim() || undefined,
      }),
    onSuccess,
    onError: (err) => {
      setSubmitError(errorMessage(err, 'Failed to update seat pool. Please try again.'))
    },
  })

  const fullNum = Number(fullSeats)
  const contribNum = Number(contribSeats)
  const valid =
    Number.isFinite(fullNum) &&
    fullNum >= 0 &&
    Number.isFinite(contribNum) &&
    contribNum >= 0 &&
    fullNum >= (pool?.seats_allocated_full ?? 0) &&
    contribNum >= (pool?.seats_allocated_contributor ?? 0)

  return (
    <ModalShell title="Manage seats" icon={<CreditCard size={18} />} onClose={onClose}>
      <p className="text-sm text-gray-500 mb-4">
        Set the total number of seats purchased for your organisation. You cannot set a total below
        the number already allocated.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="buy-full">
            Full seats (total purchased)
          </label>
          <input
            id="buy-full"
            type="number"
            min={pool?.seats_allocated_full ?? 0}
            value={fullSeats}
            onChange={(e) => setFullSeats(e.target.value)}
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            {pool?.seats_allocated_full ?? 0} currently allocated.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="buy-contrib">
            Contributor seats (total purchased)
          </label>
          <input
            id="buy-contrib"
            type="number"
            min={pool?.seats_allocated_contributor ?? 0}
            value={contribSeats}
            onChange={(e) => setContribSeats(e.target.value)}
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            {pool?.seats_allocated_contributor ?? 0} currently allocated.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="buy-plan">
            Plan tier
          </label>
          <input
            id="buy-plan"
            type="text"
            value={planTier}
            onChange={(e) => setPlanTier(e.target.value)}
            placeholder="e.g. growth"
            className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-iflow-500"
          />
        </div>

        {!valid && (
          <p className="text-xs text-amber-700">
            Totals must be non-negative and at least the number of seats already allocated.
          </p>
        )}

        {submitError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
            <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{submitError}</p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            setSubmitError(null)
            if (valid) save.mutate()
          }}
          disabled={!valid || save.isPending}
          className="bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {save.isPending ? 'Saving…' : 'Save seats'}
        </button>
      </div>
    </ModalShell>
  )
}

// ---------------------------------------------------------------------------
// Shared light-theme modal shell
// ---------------------------------------------------------------------------

function ModalShell({
  title,
  icon,
  onClose,
  children,
}: {
  title: string
  icon: ReactNode
  onClose: () => void
  children: ReactNode
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl border border-iflow-100 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-iflow-100 text-iflow-700 flex items-center justify-center">
              {icon}
            </div>
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-500 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}
